import React from "react"
import PhoneIcon from "@mui/icons-material/PhoneIphone"
import PersonIcon from "@mui/icons-material/TagFaces"
import CertifiedIcon from "@mui/icons-material/CheckCircle"
import CarIcon from "@mui/icons-material/DirectionsCar"
import DashcamIcon from "@mui/icons-material/Videocam"
import SchoolIcon from "@mui/icons-material/School"

import {
  CourseID,
  CoursePromo,
  CourseType,
  DriversEdReviews,
} from "../../../types"

const GENERIC_SYLLABUS = [
  "Chapter 1: Driving is Your Responsibility",
  "Chapter 2: Human Factors Affecting the Driver",
  "Chapter 3: Natural Forces Affecting the Automobile",
  "Chapter 4: Traffic Signs, Signals and Road Markings",
  "Chapter 5: Vehicle Systems",
  "Chapter 6: Defensive Driving, Speed, Lanes, Intersections & More",
  "Chapter 7: Sharing the Road, Causes of Accidents & Accident Prevention",
  "Chapter 8: Alcohol, Drugs & Driving",
  "Chapter 9: Licensing, Registration & Vehicle Codes",
  "Chapter 10: Earning a Driver License",
]

export const landingPageMeta: { [state: string]: CoursePromo[] } = {
  ca: [
    {
      courseType: CourseType.TeenOnline,
      pageTitle: "California Drivers Ed Online",
      hasScholarship: true,
      hasDrivingLessons: true,
      courseId: CourseID.CA_TEEN_ONLINE,
      head: "California DMV-Approved Online Drivers Ed",
      subhead:
        "Join 46,000 other students who have enrolled in Coastline Academy Online Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers, aged 17.5 or younger, who wish to earn a California driver license.",
      intendedAudienceMore:
        "It is required by the state of California for all teenagers. Upon completion, you will receive an official DMV certificate that makes you eligible to take your written test at the DMV and earn your Learner's Permit.",
      courseOnPhone:
        "Instead of spending the 30 hours it takes to complete the driver's ed course in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. You'll get the information you need to pass your permit test and start driving.",
      practiceTests:
        "Yes! Every section ends with a practice test that you can take as many times as you need. We also have a mock test at the end that is just like the one you will take at the DMV.",
      features: [
        {
          head: "Earn your DMV Certificate",
          text: "When you graduate from our teen Drivers Ed course, we will mail you a DMV Certificate of Completion that allows you to take your written test at the DMV.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the California DMV",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV (license #E2115).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Unlimited practice tests",
          text: "We want you to pass your written test as much as you do, so we provide unlimited practice tests, and even a mock DMV written test, free!",
          icon: <SchoolIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "The first step before scheduling state-required driving lessons.",
          url: "https://www.dmv.ca.gov/portal/driver-licenses-identification-cards/driver-licenses-dl/",
        },
        {
          text: "DMV approved Certificate of Completion mailed upon course completion",
        },
        {
          text: "DMV-certified, license #E2115",
        },
        {
          text: "Created by California's top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      courseType: CourseType.TeenClassroom,
      pageTitle:
        "California Classroom Drivers Ed - Fresno & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.CA_TEEN_CLASSROOM_FRESNO,
      head: "California DMV-Approved Classroom Drivers Ed",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Fresno area, aged 17.5 or younger, who wish to earn a California driver license.",
      intendedAudienceMore:
        "Drivers Ed is required by the state of California for all teenagers. Upon completion, you will receive an official DMV certificate that makes you eligible to take your written test at the DMV and earn your Learner's Permit.",
      features: [
        {
          head: "Earn your DMV Certificate",
          text: "When you graduate from our teen Drivers Ed course, you will receive a DMV Certificate of Completion that allows you to take your written test at the DMV.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the California DMV",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV (license #E2115).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Unlimited practice tests",
          text: "We want you to pass your written test as much as you do, so we provide unlimited practice tests, and even a mock DMV written test, free!",
          icon: <SchoolIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Fresno classroom",
        },
        {
          text: "The first step before scheduling state-required driving lessons.",
          url: "https://www.dmv.ca.gov/portal/driver-licenses-identification-cards/driver-licenses-dl/",
        },
        {
          text: "Certificate of Completion included",
        },
        {
          text: "Created by California's top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "DMV-certified, license #E2115",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      courseType: CourseType.TrafficSchool,
      pageTitle: "California Traffic School Course Only",
      hasScholarship: false,
      hasDrivingLessons: false,
      courseId: CourseID.CA_TRAFFIC_SCHOOL,
      head: "California DMV-Approved Traffic School Course",
      subhead:
        "Coastline is proud to offer online traffic school courses through a collaboration with MyImprov Traffic School!",
      intendedAudience:
        "This course is intended for drivers of any age who need to attend traffic school.",
      intendedAudienceMore:
        "Within 30 minutes of completion, you will receive an official certificate and your results will be automatically forwarded to the California courts. This will allow you to continue the process of restoring your driving privilege or making amendments to your driving record.",
      courseOnPhone:
        "Instead of spending the 30 hours it takes to complete the driver's ed course in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. You'll get the information you need to pass your permit test and start driving.",
      features: [
        {
          head: "Earn your Completion Certificate",
          text: "When you graduate from our online traffic school course, we will send the California courts your Certificate of Completion that will allow you to move forward in the traffic school process.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the California DMV & Accepted by all CA Courts",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV, in addition to being accepted by all California courts.",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit. You’ll get your certificate within 30 minutes of finishing your course and your results will be automatically sent to the California courts.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our customer support team is here to help you with any questions you have about our course.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [],
      syllabus: [],
    },
  ],
  co: [
    {
      courseType: CourseType.TeenOnline,
      pageTitle: "Colorado Drivers Ed Online",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.CO_TEEN_ONLINE,
      head: "Colorado DMV-Approved Online Drivers Ed",
      subhead:
        "Join 46,000 other students who have enrolled in Coastline Academy Online Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers, under 16, who wish to earn a Colorado learner permit.",
      intendedAudienceMore:
        "It is required by the state of Colorado for all teenagers under 15 and a half, and is the only online option for those under 16. Upon completion, we enter your information into the DMV database so you can get your Learner's Permit.",
      courseOnPhone:
        "Instead of spending the 30 hours it takes to complete the driver's ed course in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. You'll get the information you need to pass your permit test and start driving.",
      practiceTests:
        "Yes! Every section ends with a practice test that you can take as many times as you need. We also have a mock test at the end that is just like the one you will take at the DMV.",
      syllabus: GENERIC_SYLLABUS,
      headerPromoList: [
        {
          text: "Brand new, up-to-date content",
        },
        {
          text: "Created by Colorado's top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "DMV-certified, license #9906",
        },
      ],
      features: [
        {
          head: "Unlock your Learner's Permit",
          text: "When you graduate from our teen Drivers Ed course, we enter your completion into the DMV database so you can get your Learner's Permit.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the Colorado DMV",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV (license #9906).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Use any device: mobile, tablet, computer",
          text: "Our course is designed to make it convenient to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
    {
      courseType: CourseType.TeenClassroom,
      pageTitle:
        "Colorado Classroom Drivers Ed - Arvada & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.CO_TEEN_CLASSROOM_ARVADA,
      regionIds: ["Arvada"],
      head: "Colorado DMV-Approved Classroom Drivers Ed",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Arvada area, under 16, who wish to earn a Colorado learner permit.",
      intendedAudienceMore:
        "It is required by the state of Colorado for all teenagers under 15 and a half. Upon completion, we enter your information into the DMV database so you can get your Learner's Permit.",
      features: [
        {
          head: "Complete DMV Requirements",
          text: "When you graduate from our teen Drivers Ed course, we will enter your information into the DMV system, which allows you to get your permit.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the Colorado DMV",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV.",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Arvada classroom",
        },
        {
          text: "Created by Colorado's top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      courseType: CourseType.TeenClassroom,
      pageTitle:
        "Colorado Classroom Drivers Ed - Littleton & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.CO_TEEN_CLASSROOM_LITTLETON,
      regionIds: ["Littleton"],
      head: "Colorado DMV-Approved Classroom Drivers Ed",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Littleton area, under 16, who wish to earn a Colorado learner permit.",
      intendedAudienceMore:
        "It is required by the state of Colorado for all teenagers under 15 and a half. Upon completion, we enter your information into the DMV database so you can get your Learner's Permit.",
      features: [
        {
          head: "Complete DMV Requirements",
          text: "When you graduate from our teen Drivers Ed course, we will enter your information into the DMV system, which allows you to get your permit.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the Colorado DMV",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV.",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Littleton classroom",
        },
        {
          text: "Created by Colorado's top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
  ],
  in: [
    {
      courseType: CourseType.TeenOnline,
      pageTitle: "Indiana Drivers Ed Online",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.IN_TEEN_ONLINE,
      head: "Indiana BMV-Approved Online Drivers Ed",
      subhead:
        "We've joined forces with Driving Brilliance to offer you a premium online course experience approved by the BMV",
      intendedAudience:
        "This course is intended for new drivers who wish to earn a Indiana learner's permit.",
      intendedAudienceMore:
        "It is required by the state of Indiana for all teenagers. Once you purchase lessons, you will receive your CDE, so you can take your written test at the BMV and earn your learner's permit.",
      courseOnPhone:
        "Instead of spending the 30 hours it takes to complete the driver's ed course in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. You'll get the information you need to pass your permit test and start driving.",
      practiceTests:
        "Yes! We want to make sure you are ready for your written test at the BMV, so we provide assignments and tests to prepare you.",
      isExternalCourse: true,
      syllabus: GENERIC_SYLLABUS, // TODO
      headerPromoList: [
        {
          text: "Approved by the Indiana BMV",
        },
        {
          text: "Includes completion certificate required to get learner's permit",
        },
        {
          text: "Accessibility compliant with screen reader-ready PDFs",
        },
        {
          text: "Self-paced course, within state requirements",
        },
      ],
      features: [
        {
          head: "Course approved by the Indiana BMV",
          text: "The course is approved and certified by the BMV.",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Go at your own pace for up to three hours per day, in compliance with Indiana state law.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff is ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
  ],
  il: [
    {
      courseType: CourseType.TeenClassroom,
      pageTitle: "Illinois Classroom Drivers Ed",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.IL_TEEN_ONLINE,
      head: "Illinois Teen Classroom Drivers Ed",
      subhead:
        "Join our classroom Teen Driver Program to start earning your license",
      intendedAudience:
        "This course is intended for new drivers who wish to earn an Illinois Driver License.",
      intendedAudienceMore:
        "It is required by the state of Illinois for all teenagers. Our all-in-one Teen Driver Program includes 30 hours of classroom instruction and 8 hours of behind the wheel lessons.",
      isExternalCourse: false,
      isClassroomCourse: true,
      syllabus: [],
      headerPromoList: [
        {
          text: "In-person at our Arlington Heights classroom",
        },
        {
          text: "30 hours of engaging classroom instruction",
        },
        {
          text: "For teens 15-17 years of age",
        },
        {
          text: "Receive your permit application as early as the first day of class",
        },
        {
          text: "Drive with your parents upon receiving your permit!",
        },
        {
          text: "Behind the wheel availability guaranteed",
        },
      ],
      features: [
        {
          head: "Course approved by the Illinois DMV",
          text: "The course is approved and certified by the DMV.",
          icon: <CertifiedIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff is ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
  ],
  nv: [
    {
      courseType: CourseType.TeenOnline,
      pageTitle: "Nevada Teen Drivers Ed Online",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.NV_TEEN_ONLINE,
      head: "Nevada Online Teen Drivers Ed",
      subhead:
        "Join 46,000 other students who have enrolled in Coastline Academy Online Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers who wish to earn a Nevada driver license.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Nevada's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      courseOnPhone:
        "Instead of spending hours in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. It’s up to you, take the course how you want.",
      practiceTests:
        "Yes! We know that practice makes perfect. That's why most chapters end with an interactive quiz so you can have the confidence to get behind the wheel.",
      syllabus: GENERIC_SYLLABUS,
      headerPromoList: [
        {
          text: "Online course for mobile, desktop and tablet",
        },
        {
          text: "Nevada DMV-approved",
        },
        {
          text: "Go at your own pace",
        },
        {
          text: "Required before you take your driving test",
        },
      ],
      features: [
        {
          head: "Earn your DMV Certificate",
          text: "When you graduate from our teen Drivers Ed course, you can download your completion certificate that you take to your driving test at the DMV.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the Nevada DMV",
          text: "Our course is developed by certified driving instructors and is approved and certified by the DMV (license #PRDS00051916 in Las Vegas, #PRDS00051767 in and around Reno).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
  ],
  mo: [
    {
      courseType: CourseType.TeenOnline,
      pageTitle: "Missouri Teen Drivers Ed Online",
      hasScholarship: false,
      hasDrivingLessons: false,
      courseId: CourseID.MO_TEEN_ONLINE,
      head: "Missouri Teen Online Drivers Ed",
      subhead:
        "Join 46,000 other students who have enrolled in Coastline Academy Online Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers who wish to learn to drive in Missouri.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Missouri's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      courseOnPhone:
        "Instead of spending hours in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. It’s up to you, take the course how you want.",
      practiceTests:
        "Yes! We know that practice makes perfect. That's why most chapters end with an interactive quiz so you can have the confidence to get behind the wheel.",
      syllabus: GENERIC_SYLLABUS,
      headerPromoList: [
        {
          text: "Online course for mobile, desktop and tablet",
        },
        {
          text: "Approved by multiple US states",
        },
        {
          text: "Go at your own pace",
        },
        {
          text: "No timers, just quality content",
        },
      ],
      features: [
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to become a safer driver.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
  ],
  tx: [
    {
      courseType: CourseType.TeenOnline,
      pageTitle: "Texas 32-Hour Teen Drivers Ed Course",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_TEEN_ONLINE,
      head: "Texas 32-Hour Teen Online Drivers Ed",
      subhead:
        "For teens aged 14-17 looking to enroll in the instructor-led 7x7 program",
      intendedAudience:
        "This course is intended for new drivers aged 14-17 who wish to learn to drive in Texas.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place. Note: Our course is built on the block method, which means that it must be completed in its entirety in order to obtain your permit.",
      courseOnPhone:
        "Instead of spending hours in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. It’s up to you, take the course how you want.",
      practiceTests:
        "Yes! We know that practice makes perfect. That's why most chapters end with an interactive quiz so you can have the confidence to get behind the wheel.",
      syllabus: GENERIC_SYLLABUS,
      bannerText: "Our Most Popular Teen Option!",
      isFeaturedCourse: true,
      headerPromoList: [
        {
          text: "Online course for mobile, desktop and tablet",
        },
        {
          text: "Approved by the Texas Department of Licensing and Regulation (C3371)",
        },
        {
          text: "Certificate issued to you within 24-48 hours of course completion",
        },
        {
          text: "Required before you take your driving test",
        },
        {
          text: "Does not offer the Class C test at this time",
        },
      ],
      comparisonFeatures: [
        "For Teens Ages 14-17",
        "Self-Paced Course",
        "14 Hours of Professional Driving Instruction Required (7 hours driving + 7 hours observing)",
        "Shared Lessons Start at Designated Meeting Points",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our teen Drivers Ed course, we will email you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
    {
      courseType: CourseType.ParentTaughtOnline,
      pageTitle: "Texas Parent-Taught Drivers Ed Course",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_TEEN_PARENT_ONLINE,
      head: "Texas Parent-Taught Online Drivers Ed",
      subhead:
        "For teens aged 14-17 looking to enroll in the parent-led driver education program",
      intendedAudience:
        "This course is intended for new drivers aged 14-17 who wish to learn to drive in Texas.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      courseOnPhone:
        "Instead of spending hours in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. It’s up to you, take the course how you want.",
      practiceTests:
        "Yes! We know that practice makes perfect. That's why most chapters end with an interactive quiz so you can have the confidence to get behind the wheel.",
      syllabus: GENERIC_SYLLABUS,
      headerPromoList: [
        {
          text: "Online course for mobile, desktop and tablet",
        },
        {
          text: "Approved by the Texas Department of Licensing and Regulation (C3371)",
        },
        {
          text: "Certificate issued to you within 24-48 hours of course completion",
        },
        {
          text: "Required before you take your driving test",
        },
        {
          text: "Does not offer the Class C test at this time",
        },
      ],
      isFeaturedCourse: false,
      comparisonFeatures: [
        "For Teens Ages 14-17",
        "Self-Paced Course",
        "Parent-Led Driving Instruction",
        "Professional Behind-the-Wheel Instruction Optional, but Recommended",
        "Private Lessons with Home Pickup Available",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our parent-taught Drivers Ed course, we will email you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
    {
      courseType: CourseType.AdultOnline,
      pageTitle: "Texas 6-Hour Adult Drivers Ed Course",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_ADULT_ONLINE,
      head: "Texas 6-Hour Adult Online Drivers Ed",
      subhead:
        "For adults aged 18-24 who need to fulfill the state's driver education course requirement",
      intendedAudience:
        "This course is intended for new drivers aged 18-24 who wish to learn to drive in Texas.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      courseOnPhone:
        "Instead of spending hours in a classroom, you can take it online. Better yet, with Coastline Academy you can do the entire course on your phone. Or jump back and forth between your phone, tablet, and computer. It’s up to you, take the course how you want.",
      practiceTests:
        "Yes! We know that practice makes perfect. That's why most chapters end with an interactive quiz so you can have the confidence to get behind the wheel.",
      syllabus: [
        "Chapter 1: Driving is Your Responsibility",
        "Chapter 2: State Licensing",
        "Chapter 3: State Laws",
        "Chapter 4: Defensive Driving",
        "Chapter 5: Sharing the Road, Causes of Accidents & Accident Prevention",
        "Chapter 6: Traffic Signs, Signals and Road Markings",
        "Chapter 7: Defensive Driving: Part 2",
        "Chapter 8: Human Factors Affecting the Driver",
        "Chapter 9: Alcohol & Other Drugs",
        "Chapter 10: Sharing the Road, Causes of Accidents & Accident Prevention: Part 2",
        "Chapter 11: Human Factors Affecting the Driver: Part 2",
        "Chapter 12: Sharing the Road, Causes of Accidents & Accident Prevention: Part 3",
      ],
      headerPromoList: [
        {
          text: "Online course for mobile, desktop and tablet",
        },
        {
          text: "Approved by the Texas Department of Licensing and Regulation (C3371)",
        },
        {
          text: "Certificate issued to you within 24-48 hours of course completion",
        },
        {
          text: "Required before you take your driving test",
        },
        {
          text: "Does not offer the Class C test at this time",
        },
      ],
      comparisonFeatures: [
        "For Adults 18+",
        "Self-Paced Course",
        "Professional Behind-the-Wheel Instruction Optional, but Recommended",
        "Private Lessons With Home Pickup Available",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our adult Drivers Ed course, we will email you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your license.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
    },
    {
      bannerText: "In-Person in Mansfield",
      courseType: CourseType.TeenClassroom,
      pageTitle:
        "Texas 24-Hour Classroom Drivers Ed - Mansfield & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_TEEN_CLASSROOM_MANSFIELD,
      regionIds: ["Mansfield"],
      landingPageUrl: "classroom-drivers-ed",
      head: "State-Approved Classroom Drivers Ed in Mansfield, Texas",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Mansfield area who wish to earn a Texas learner permit.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      comparisonFeatures: [
        "For Teens Ages 14-17",
        "In-person in our Mansfield Office",
        "Take your test at DPS after 6 hours of class",
        "Shared Lessons Start at Designated Meeting Points",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our teen Drivers Ed course, we will give you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Mansfield classroom",
        },
        {
          text: "Created by top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "Take your test at DPS after 6 hours of class",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      bannerText: "In-Person in Richmond",
      courseType: CourseType.TeenClassroom,
      pageTitle:
        "Texas 24-Hour Classroom Drivers Ed - Richmond & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_TEEN_CLASSROOM_RICHMOND,
      regionIds: ["Richmond TX"],
      landingPageUrl: "classroom-drivers-ed-richmond",
      head: "State-Approved Classroom Drivers Ed in Richmond, Texas",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Richmond area who wish to earn a Texas learner permit.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      comparisonFeatures: [
        "For Teens Ages 14-17",
        "In-person in our Richmond Office",
        "Take your test at DPS after 6 hours of class",
        "Shared Lessons Start at Designated Meeting Points",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our teen Drivers Ed course, we will give you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Richmond classroom",
        },
        {
          text: "Created by top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "Take your test at DPS after 6 hours of class",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      bannerText: "In-Person at Summit High School",
      courseType: CourseType.TeenClassroom,
      pageTitle: "Texas 24-Hour Classroom Drivers Ed - Summit High School Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_TEEN_CLASSROOM_SUMMIT,
      regionIds: ["Summit TX"],
      landingPageUrl: "classroom-drivers-ed-summit",
      head: "Summit High School Students! Enjoy State-Approved Classroom Drivers Ed at your school.",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Summit area who wish to earn a Texas learner permit.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      comparisonFeatures: [
        "For Teens Ages 14-17",
        "In-person at Summit High School",
        "Take your test at DPS after 6 hours of class",
        "Shared Lessons Start at Designated Meeting Points",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our teen Drivers Ed course, we will give you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at Summit High School",
        },
        {
          text: "Created by top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "Take your test at DPS after 6 hours of class",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      bannerText: "In-Person in Richmond",
      courseType: CourseType.AdultClassroom,
      pageTitle:
        "Texas 6-Hour Adult Classroom Permit Class - Richmond & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_ADULT_CLASSROOM_RICHMOND,
      regionIds: ["Richmond TX"],
      landingPageUrl: "classroom-adult-drivers-ed-richmond",
      head: "State-Approved Classroom Permit Class in Richmond, Texas",
      subhead:
        "Join thousands of other students who have enrolled in Coastline courses",
      intendedAudience:
        "The State of Texas requires 6 hours of classroom for adults 18-24 years of age.",
      intendedAudienceMore:
        "It is also available to anyone over 25 who needs additional training on basic rules and regulations, defensive driving techniques and any DPS questions you may have. If you're looking to get started behind the wheel, you've come to the right place.",
      comparisonFeatures: [
        "For Adults 18+",
        "In-person in our Richmond Office",
        "Six hours of classroom instruction",
        "Private lessons with home pickup available",
      ],
      features: [
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Richmond classroom",
        },
        {
          text: "Created by top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "Driving lesson packages available",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      bannerText: "In-Person in Fort Worth",
      courseType: CourseType.AdultClassroom,
      pageTitle:
        "Texas 6-Hour Adult Classroom Permit Class - Fort Worth & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_ADULT_CLASSROOM_FTWORTH,
      regionIds: ["Fort Worth", "Dallas"],
      landingPageUrl: "classroom-adult-drivers-ed-ftworth",
      head: "State-Approved Classroom Permit Class in Fort Worth, Texas",
      subhead:
        "Join thousands of other students who have enrolled in Coastline courses",
      intendedAudience:
        "The State of Texas requires 6 hours of classroom for adults 18-24 years of age.",
      intendedAudienceMore:
        "It is also available to anyone over 25 who needs additional training on basic rules and regulations, defensive driving techniques and any DPS questions you may have. If you're looking to get started behind the wheel, you've come to the right place.",
      comparisonFeatures: [
        "For Adults 18+",
        "In-person in our Fort Worth Office",
        "Six hours of classroom instruction",
        "Private lessons with home pickup available",
      ],
      features: [
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Richmond classroom",
        },
        {
          text: "Created by top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "Driving lesson packages available",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
    {
      bannerText: "In-Person in Fort Worth",
      courseType: CourseType.TeenClassroom,
      pageTitle:
        "Texas 24-Hour Classroom Drivers Ed - Fort Worth & Surrounding Areas Only",
      hasScholarship: false,
      hasDrivingLessons: true,
      courseId: CourseID.TX_TEEN_CLASSROOM_FTWORTH,
      regionIds: ["Fort Worth", "Dallas"],
      landingPageUrl: "classroom-drivers-ed-ftworth",
      head: "State-Approved Classroom Drivers Ed in Fort Worth, Texas",
      subhead:
        "Join thousands of other students who have enrolled in Coastline Academy Drivers Ed",
      intendedAudience:
        "This course is intended for new drivers in the Ft. Worth area who wish to earn a Texas learner permit.",
      intendedAudienceMore:
        "It walks you through all aspects of driving in America and on Texas's unique roads. If you're looking to get started behind the wheel, you've come to the right place.",
      comparisonFeatures: [
        "For Teens Ages 14-17",
        "In-person in our Ft. Worth Office",
        "Take your test at DPS after 6 hours of class",
        "Shared Lessons Start at Designated Meeting Points",
      ],
      features: [
        {
          head: "Earn your DPS Certificate",
          text: "When you graduate from our teen Drivers Ed course, we will give you a Certificate of Completion that allows you to take your written test at the DPS.",
          icon: <CarIcon />,
        },
        {
          head: "Course approved by the TDLR",
          text: "Our course is developed by certified driving instructors and is approved and certified by the TDLR (license #C3371).",
          icon: <CertifiedIcon />,
        },
        {
          head: "Video, images and text",
          text: "Everyone learns differently, so we include text, images, illustrations and video in our teen driver education course.",
          icon: <DashcamIcon />,
        },
        {
          head: "Fast and convenient",
          text: "Our course is designed to make it fast and easy for you to get the information you need to earn your permit.",
          icon: <PhoneIcon />,
        },
        {
          head: "Friendly customer support",
          text: "Our highly-trained support staff includes certified instructors, so we are ready to help with every question.",
          icon: <PersonIcon />,
        },
      ],
      headerPromoList: [
        {
          text: "In-person at our Fort Worth classroom",
        },
        {
          text: "Created by top-rated driving instructors",
        },
        {
          text: "Videos and quizzes on key topics",
        },
        {
          text: "Take your test at DPS after 6 hours of class",
        },
      ],
      syllabus: GENERIC_SYLLABUS,
    },
  ],
}

// Every state uses the same reviews right now, abstract them.
const TEMPORARY_DE_REVIEWS = [
  {
    text: "Really liked the structure and essence of the course, and it summed up the most important things in short responses.",
    name: "Andres N",
    location: "Chula Vista",
  },
  {
    text: "Everything was perfect.",
    name: "Erik T",
    location: "Glendale",
  },
  {
    text: "What I enjoyed most about the course was the unlimited test-taking attempts. It really took the stress off, and I was able to more thoroughly understand the rules.",
    name: "Catherine T",
    location: "San Jose",
  },
]

export const driversEdReviews: DriversEdReviews = {
  ca: TEMPORARY_DE_REVIEWS,
  co: TEMPORARY_DE_REVIEWS,
  mo: TEMPORARY_DE_REVIEWS,
  nv: TEMPORARY_DE_REVIEWS,
}

export function getCourseByCourseId(
  state: string,
  courseId: CourseID,
): CoursePromo {
  const matchingCourse = landingPageMeta[state.toLowerCase()].find(
    (course) => course.courseId === courseId,
  )

  if (!matchingCourse) {
    throw new Error(`No course found for course id ${courseId}`)
  }

  return matchingCourse
}

export function courseTypeFromCourseId(
  state: string,
  courseId?: CourseID,
): CourseType | null {
  if (!courseId) {
    return null
  }

  const matchingCourse = landingPageMeta[state.toLowerCase()].find(
    (course) => course.courseId === courseId,
  )

  if (!matchingCourse) {
    throw new Error(`No course found for course id ${courseId}`)
  }

  return matchingCourse.courseType
}

export function getCourseByType(
  state: string,
  courseType: CourseType,
  regionId?: string,
): CoursePromo {
  const stateCourses = landingPageMeta[state]
  if (!stateCourses) {
    throw new Error(`No course meta found for state ${state}`)
  }

  const matchingCourses = stateCourses.filter(
    (course) =>
      course.courseType === courseType &&
      (!course.regionIds || !regionId || course.regionIds.includes(regionId)),
  )

  if (matchingCourses.length === 0) {
    throw new Error(`No course found for ${courseType} in ${state}`)
  }

  if (matchingCourses.length === 1) {
    return matchingCourses[0]
  }

  // This shouldn't be possible as if we have multiple courses in the state
  // by type, we should also be passing in a regionId.
  throw new Error(`Multiple course IDs found for ${courseType} in ${state}`)
}

export const DE_AVAILABLE_STATES = ["CA", "CO", "IN", "IL", "MO", "NV", "TX"]

export const IL_HELPFUL_LINKS = [
  {
    title: "Classroom Course Materials",
    url: "/article/illinois-classroom-materials",
  },
  {
    title: "Drivers Education Course Completion Check",
    url: "https://www.ilsos.gov/deccheck/",
  },
  {
    title: "Free Permit Practice Tests",
    url: "http://m.driving-tests.org/illinois/",
  },
  {
    title: "Illinois Rules of the Road",
    url: "http://www.cyberdriveillinois.com/publications/pdf_publications/dsd_a112.pdf",
  },
  {
    title: "50 Hours Practice Driving Log Under 18 Applicant",
    url: "http://www.cyberdriveillinois.com/publications/pdf_publications/dsd_x152.pdf",
  },
  {
    title: "Graduated License Rules",
    url: "http://www.cyberdriveillinois.com/departments/drivers/teen_driver_safety/gdl.html",
  },
  {
    title: "Instruction Permit Requirements",
    url: "http://www.cyberdriveillinois.com/departments/drivers/driver_education/instructpermit.html",
  },
  {
    title: "Parent Teen Driving Guide",
    url: "http://www.cyberdriveillinois.com/publications/pdf_publications/dsd_a217.pdf",
  },
  {
    title: "Acceptable Identification",
    url: "http://www.cyberdriveillinois.com/publications/pdf_publications/dsd_x173.pdf",
  },
  {
    title: "Illinois Permit Allowed by State",
    url: "https://docs.wixstatic.com/ugd/7d05a0_56105e97a4e74cb18fc161eb4bd2bb84.pdf",
  },
  {
    title: "Parental Teen Drivers License Access",
    url: "https://www.ilsos.gov/parentalaccess/",
  },
  {
    title: "Center for Disease Control Teen Driver Parental Advice",
    url: "http://www.cdc.gov/parentsarethekey/",
  },
  {
    title: "Parent Teen Written Driving Agreement",
    url: "http://www.cyberdriveillinois.com/publications/pdf_publications/dsd_a211.pdf",
  },
  {
    title: "Drivers Education Approval Form for the High School",
    url: "https://www.ilsos.gov/publications/pdf_publications/dsd_cdts54.pdf",
  },
]
